/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from 'react';
import type { TFunction } from 'i18next';
import { FaPlus, FaRegFile, FaSave, FaTrashAlt } from 'react-icons/fa';
import U2000ActionBtn from '../../../../u2000Core/components/buttons/U2000ActionBtn';
import U2000ResponsiveToolbar from '../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar';
import U2000DetailTable from '../../../../u2000Core/components/tables/detail/U2000DetailTable';
import { U2000IdColumnDef, type U2000TableColumn } from '../../../../u2000Core/components/tables/basic/U2000BasicTableList';
import { useU2000AuthContext } from '../../../../u2000Core/context/auth/U2000UseAuthContext';
import type { U2000FormStateHandlers } from '../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { useU2000GridStateHandlers } from '../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import { u2000CanEditMissionDefaultPrompts } from '../../../../u2000Core/tools/U2000SystemScopeAccess';
import { NS, U3203ROLES } from '../../../U3203';
import { useU3203DefaultPromptsHandlers } from '../../../hooks/useU3203DefaultPromptsHandlers';
import {
  U3203_MAX_ACTIVE_DEFAULT_PROMPTS,
  u3203CountActiveDefaultPrompts,
  u3203DefaultPromptsFingerprint,
  u3203IsPersistedMissionId,
  u3203NextDefaultPromptSortOrder,
  type U3203AiMissionFormRow,
  type U3203DefaultPrompt,
} from '../../../U3203Service';
import U3203AiMissionDefaultPromptDlg from './U3203AiMissionDefaultPromptDlg';

interface U3203AiMissionDefaultPromptsTabProps {
  formStateHandlers: U2000FormStateHandlers<U3203AiMissionFormRow>;
  t: TFunction;
}

function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    { ...U2000IdColumnDef },
    {
      name: 'sortOrder',
      label: t('lblSortOrder', { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: 'w-[12%]',
      type: 'number',
      align: 'center',
    },
    {
      name: 'labelFr',
      label: t('lblLabelFr', { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: 'w-[34%]',
      type: 'string',
      align: 'left',
    },
    {
      name: 'labelEn',
      label: t('lblLabelEn', { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: 'w-[34%]',
      type: 'string',
      align: 'left',
    },
    {
      name: 'active',
      label: t('lblActive', { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: 'w-[20%]',
      type: 'boolean',
      align: 'center',
      display: (_column, value) => {
        if (value === true) return t('lblActiveYes', { ns: NS });
        if (value === false) return t('lblActiveNo', { ns: NS });
        return null;
      },
    },
  ];
}

function U3203AiMissionDefaultPromptsTab({ formStateHandlers, t }: U3203AiMissionDefaultPromptsTabProps) {
  const { auth, hasRole } = useU2000AuthContext();
  const { replaceDefaultPrompts, saving } = useU3203DefaultPromptsHandlers();
  const data = formStateHandlers.getData();
  const items = data?.defaultPrompts ?? [];
  const isPersisted = u3203IsPersistedMissionId(data?.id);
  const isSystemMission = data?.systemScope === true;
  const sessionOrganizationId = auth.user?.organizationId;
  const canEditDefaultPrompts = u2000CanEditMissionDefaultPrompts({
    isSystemScope: isSystemMission,
    hasEditRole: hasRole(U3203ROLES.EDIT),
    sessionOrganizationId,
  });
  const canEdit = Boolean(canEditDefaultPrompts && isPersisted);
  const gridStateHandlers = useU2000GridStateHandlers<U3203DefaultPrompt>();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [draft, setDraft] = useState<U3203DefaultPrompt | null>(null);

  const itemsDataKey = `${data?.id ?? ''}_${u3203DefaultPromptsFingerprint(items)}`;

  const readItems = useCallback((): U3203DefaultPrompt[] => {
    return formStateHandlers.getData()?.defaultPrompts ?? [];
  }, [formStateHandlers]);

  const setItems = useCallback(
    (next: U3203DefaultPrompt[]) => {
      const current = formStateHandlers.getData();
      if (!current) return;
      const sorted = [...next].sort((a, b) => a.sortOrder - b.sortOrder);
      formStateHandlers.setData({ ...current, defaultPrompts: sorted });
    },
    [formStateHandlers],
  );

  useEffect(() => {
    const columns = getColumnsDef(t);
    gridStateHandlers.setColumns(columns);
    gridStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
    gridStateHandlers.setSelectedSortedFields(
      columns
        .filter((col) => col.isDefaultSortedColumn)
        .map((col) => ({ name: col.name, sortOrder: 'ASC' as const })),
    );
    const list = [...items].sort((a, b) => a.sortOrder - b.sortOrder);
    gridStateHandlers.setData(list);
    gridStateHandlers.setTotalRows(list.length);
    gridStateHandlers.setErrors([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- gridStateHandlers volontairement exclu
  }, [itemsDataKey, t]);

  const selectedRows = gridStateHandlers.getSelectedRows();
  const addDisabled = !canEdit || saving;
  const editDisabled = !canEdit || saving || selectedRows.length !== 1;
  const deleteDisabled = !canEdit || saving || selectedRows.length === 0;
  const saveDisabled = !canEdit || saving;

  const openDraft = (mode: 'add' | 'edit', item: U3203DefaultPrompt) => {
    setDialogMode(mode);
    setDraft({ ...item });
    setDialogOpen(true);
  };

  const handleAdd = () => {
    if (addDisabled) return;
    const currentItems = readItems();
    openDraft('add', {
      id: `tmp-${crypto.randomUUID()}`,
      labelFr: '',
      labelEn: '',
      promptFr: '',
      promptEn: '',
      sortOrder: u3203NextDefaultPromptSortOrder(currentItems),
      active: u3203CountActiveDefaultPrompts(currentItems) < U3203_MAX_ACTIVE_DEFAULT_PROMPTS,
    });
  };

  const handleEdit = () => {
    if (editDisabled) return;
    const selectedId = selectedRows[0]?.id;
    const full = readItems().find((item) => item.id === selectedId);
    if (!full) return;
    openDraft('edit', full);
  };

  const handleDelete = () => {
    if (deleteDisabled) return;
    const selectedIds = new Set(selectedRows.map((row) => row.id));
    setItems(readItems().filter((item) => !selectedIds.has(item.id)));
  };

  const persistCollection = async (nextItems: U3203DefaultPrompt[]) => {
    const current = formStateHandlers.getData();
    if (!current?.id || !u3203IsPersistedMissionId(current.id)) return false;
    if (u3203CountActiveDefaultPrompts(nextItems) > U3203_MAX_ACTIVE_DEFAULT_PROMPTS) {
      return false;
    }
    const saved = await replaceDefaultPrompts(current.id, nextItems);
    if (!saved) return false;
    const latest = formStateHandlers.getData();
    if (latest) {
      formStateHandlers.setData({
        ...latest,
        defaultPrompts: saved.defaultPrompts,
      });
    }
    return true;
  };

  const handleDialogSave = async (item: U3203DefaultPrompt) => {
    const without = readItems().filter((row) => row.id !== item.id);
    const next = [...without, item].sort((a, b) => a.sortOrder - b.sortOrder);
    setItems(next);
    await persistCollection(next);
  };

  const handleSaveCollection = async () => {
    if (saveDisabled) return;
    await persistCollection(readItems());
  };

  const actions = [
    {
      key: 'add',
      label: t('btnAdd', { ns: NS }),
      handleClick: handleAdd,
      hasRole: hasRole(U3203ROLES.EDIT),
      disabled: addDisabled,
      icon: FaPlus,
      Component: U2000ActionBtn,
    },
    {
      key: 'edit',
      label: t('btnModify', { ns: NS }),
      handleClick: handleEdit,
      hasRole: hasRole(U3203ROLES.EDIT),
      disabled: editDisabled,
      icon: FaRegFile,
      Component: U2000ActionBtn,
    },
    {
      key: 'delete',
      label: t('btnDelete', { ns: NS }),
      handleClick: handleDelete,
      hasRole: hasRole(U3203ROLES.EDIT),
      disabled: deleteDisabled,
      icon: FaTrashAlt,
      Component: U2000ActionBtn,
    },
    {
      key: 'save',
      label: t('btnSave', { ns: NS }),
      handleClick: () => {
        void handleSaveCollection();
      },
      hasRole: hasRole(U3203ROLES.EDIT),
      disabled: saveDisabled,
      icon: FaSave,
      Component: U2000ActionBtn,
    },
  ];

  return (
    <div className="mt-2">
      {!isPersisted ? (
        <p className="mb-2 text-sm text-amber-700">{t('msgSaveMissionFirst', { ns: NS })}</p>
      ) : null}

      <U3203AiMissionDefaultPromptDlg
        isOpen={dialogOpen}
        mode={dialogMode}
        draft={draft}
        existingItems={items}
        t={t}
        onClose={() => {
          setDialogOpen(false);
          setDraft(null);
        }}
        onSave={(item) => {
          void handleDialogSave(item);
        }}
      />

      <div className="mt-1 mb-1 flex items-center gap-x-4 border-b-2 pb-3">
        <div className="min-w-0 w-3/4 flex-1">
          <U2000ResponsiveToolbar actions={actions} />
        </div>
        <p className="w-1/4 shrink-0 self-center text-right text-sm leading-5 text-muted-foreground">
          {t('defaultPrompts.help', { ns: NS })} {t('defaultPrompts.limit', { ns: NS })}
        </p>
      </div>
      <U2000DetailTable
        gridStateHandlers={gridStateHandlers}
        allowRowSelection={canEdit}
        rowClickFn={
          canEdit
            ? (row) => {
                const match = readItems().find((item) => item.id === String(row.id));
                if (!match) return;
                openDraft('edit', match);
              }
            : undefined
        }
        label={t('lblDefaultPromptList', { count: gridStateHandlers.getTotalRows(), ns: NS })}
      />
    </div>
  );
}

export default U3203AiMissionDefaultPromptsTab;
